import { useEffect, useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { api } from "../services/api";
import { Link } from "react-router-dom";

const AdminEarlyPitchers = () => {
  const [applications, setApplications] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const limit = 20;
  const [searchQuery, setSearchQuery] = useState("");

  const fetchData = async (pageToLoad = 1, status = "", query = "") => {
    const params = new URLSearchParams({
      page: pageToLoad,
      limit,
      ...(status && { status }),
      ...(query && { q: query })
    }).toString();

    const res = await api.get(`/api/admin/early-pitchers/list?${params}`);
    setApplications(res.data.applications);
    setPage(res.data.page);
    setTotal(res.data.total);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalPages = Math.ceil(total / limit);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortDir("asc");
    }
  };

  const sortedApplications = [...applications].sort((a, b) => {
    const aVal = a[sortBy] ?? "";
    const bVal = b[sortBy] ?? "";

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }

    const aStr = aVal.toString().toLowerCase();
    const bStr = bVal.toString().toLowerCase();

    if (aStr < bStr) return sortDir === "asc" ? -1 : 1;
    if (aStr > bStr) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return (
    <Box sx={{
      backgroundImage: 'url("/assets/bg-pitchest.jpg")',
      backgroundSize: "cover",
      minHeight: "100vh",
      px: 2,
      py: 6,
    }}>
      <Box
        sx={{
          backgroundColor: "rgba(0, 0, 0, 0.6)",
          borderRadius: 4,
          padding: { xs: 2, md: 4 },
          color: "white",
        }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
          <Typography variant="h5">🚀 Early Pitcher Applications</Typography>
          <Link to="/admin" style={{ color: "#90caf9", fontWeight: "bold" }}>⬅ Back</Link>
        </Box>

        <Typography variant="body2" sx={{ mb: 2 }}>
          Total: {total} — Pending: {applications.filter(a => a.status === "pending").length} — Page {page} of {totalPages}
        </Typography>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 2 }}>
        <Box sx={{ mb: 1 }}>
          <input
            type="text"
            placeholder="Email, First or Last Name"
            value={searchQuery}
            style={{
              padding: "6px 10px",
              width: 250,
              borderRadius: 4,
              border: "1px solid #888",
              backgroundColor: "#222",
              color: "#fff",
              marginRight: 8,
            }}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                fetchData(1, statusFilter, searchQuery);
              }
            }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => fetchData(1, statusFilter, searchQuery)}
          >
            🔍 Search
          </Button>
        </Box>

        <Box sx={{ mb: 2 }}>
          <label style={{ marginRight: 8 }}>Filter by status:</label>
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              fetchData(1, e.target.value, searchQuery);
            }}
            style={{ padding: "6px 10px", backgroundColor: "#222", color: "#fff", border: "1px solid #888", borderRadius: "4px" }}
          >
            <option value="">All</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
          </select>
        </Box>
      </Box>
        <Box sx={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", color: "white" }}>
            <thead>
              <tr>
                {[
                  { label: "Date", field: "created_at" },
                  { label: "Name", field: "name" },
                  { label: "Email", field: "email" },
                  { label: "Project", field: "project_name" },
                  { label: "Stage", field: "stage" },
                  { label: "Fundraising", field: "fundraising" },
                  { label: "Status", field: "status" }
                ].map(({ label, field }) => (
                  <th
                    key={field}
                    onClick={() => handleSort(field)}
                    style={{
                      cursor: "pointer",
                      padding: "8px",
                      backgroundColor: "#34163A"
                    }}
                  >
                    {label} {sortBy === field && (sortDir === "asc" ? "▲" : "▼")}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedApplications.map((a, idx) => (
                <tr key={a.id} style={{ backgroundColor: idx % 2 === 0 ? "#5E2F6E" : "transparent" }}>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{new Date(a.created_at).toLocaleString()}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{a.name}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{a.email}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{a.project_name}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{a.stage}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>{a.fundraising}</td>
                  <td style={{ padding: "8px", textAlign:"center"  }}>
                    <select
                      value={a.status}
                      onChange={async (e) => {
                        const newStatus = e.target.value;
                        await api.patch(`/api/admin/early-pitchers/${a.id}`, { status: newStatus });
                        fetchData(page, statusFilter, searchQuery);
                      }}
                      style={{
                        backgroundColor:
                          a.status === "pending" ? "#b71c1c" :
                          a.status === "accepted" ? "#1b5e20" :
                          "#555",
                        color: "#fff", border: "1px solid #888", borderRadius: "4px", padding: "4px 8px"
                      }}
                    >
                      <option value="pending">Pending</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                    </select>
                  </td>
                </tr>
              ))}
              {sortedApplications.length === 0 && (
                <tr>
                  <td colSpan={7} style={{ padding: "16px", textAlign: "center", backgroundColor: "#452654" }}>
                    No data found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </Box>

        <Box sx={{ mt: 3, display: "flex", gap: 2 }}>
          <Button variant="outlined" disabled={page === 1} onClick={() => fetchData(page - 1, statusFilter, searchQuery)}>⬅ Prev</Button>
          <Button variant="outlined" disabled={page === totalPages || applications.length === 0} onClick={() => fetchData(page + 1, statusFilter, searchQuery)}>Next ➡</Button>
        </Box>
      </Box>
    </Box>
  );
};

export default AdminEarlyPitchers;
